import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, post } from "@/lib/api";
import { formatDate, formatTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { ImportCounts } from "./ImportFixStep";

export const ImportReviewStep = ({ items, result, loading, onBack, onCreated }) => {
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      onCreated(await post("/runs/import/commit", { rows: items }));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="import-review-step">
      <ImportCounts result={result} loading={loading} />
      {result?.blocking && <p className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="import-blocking">{result.blocking}</p>}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr>{["Date", "Start", "Locksmith", "Officer", "Addresses"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(result?.runs || []).map((r) => (
              <tr key={r.key} data-testid="import-review-run">
                <td className="px-3 py-2">{formatDate(r.run_date)}</td>
                <td className="px-3 py-2 font-data">{formatTime(r.start_time)}</td>
                <td className="px-3 py-2">{r.locksmith?.name || <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">Open job</span>}</td>
                <td className="px-3 py-2">{r.officer_name}{r.new_officer && <span className="ml-1 rounded bg-blue-50 px-1 text-xs text-blue-800">New officer</span>}</td>
                <td className="px-3 py-2 font-data">{r.address_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result?.new_officers.length > 0 && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm" data-testid="import-new-officers">
          <p className="font-semibold text-blue-900">New officers to add ({result.new_officers.length})</p>
          <ul className="mt-1 text-blue-900">{result.new_officers.map((o) => <li key={o.key}>{o.name} · {o.phone || "no phone"} · {o.company}</li>)}</ul>
        </div>
      )}
      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={onBack} disabled={busy} data-testid="import-review-back">Back</Button>
        <Button onClick={create} disabled={busy || loading || !result?.runs.length || !!result?.blocking || result?.counts.problems > 0} className="bg-blue-700 hover:bg-blue-800" data-testid="import-create-button">
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Create {result?.runs.length || 0} run{result?.runs.length === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
};

export const ImportDone = ({ done, onClose }) => (
  <div className="space-y-4" data-testid="import-done">
    <p className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 className="h-5 w-5" />
      {done.runs.length} run{done.runs.length === 1 ? "" : "s"} created, {done.new_officers} new officer{done.new_officers === 1 ? "" : "s"} added, {done.skipped} row{done.skipped === 1 ? "" : "s"} skipped
    </p>
    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
      {done.runs.map((r) => (
        <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2">
          <span>{formatDate(r.run_date)} · {formatTime(r.start_time)} · {r.locksmith_name || "Open job"} · {r.address_count} address{r.address_count === 1 ? "" : "es"}</span>
          <Link to={`/manager/runs/${r.id}`} className="font-semibold text-blue-700 hover:underline" data-testid={`import-done-link-${r.id}`}>Open</Link>
        </li>
      ))}
    </ul>
    <div className="flex justify-end"><Button onClick={onClose} data-testid="import-done-close">Done</Button></div>
  </div>
);
